import { ForwardRefRenderFunction, forwardRef, useCallback } from "react";
//#region types
import { CurrencyEnums } from "models/enums";
import { BenefitFormTypes } from "models/formTypes";
import { BenefitListProps } from "models/propTypes/benefitsPropTypes";
import { StyledDateRange, YearMonth } from "models/propTypes/commonPropTypes";
import { TableHandles } from "models/propTypes/simpleTableProps";
import { Amount } from "models/utilTypes/currencyType";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelWintSalaryBenefits, WintUtilsWebApi } from "wint-api-client/dist/types";
//#endregion
//#region components
import { TableAsync, useFilterContext } from "components/common";
import { MultiPersonNames } from "components/common/profile";
import { useBenefitService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance, TableHelper } from "utils/helpers";
import { CommonConstants } from "utils/constants";
import { camelize } from "humps";
import cssExports from "theme/styles/main.module.scss";
import useBenefitDialog from "./forms/useBenefitDialog";
import { getBenefitTypeName } from "./forms/useBenefitTypesHelper";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";

const BenefitTypeProperty = "BenefitType";
const TypeSortFetchCap = 1000;

const BenefitList: ForwardRefRenderFunction<TableHandles, BenefitListProps> = (props, ref) => {
  const { reloadKey, reloadList } = props;

  const benefitService = useBenefitService();
  const dispatch = useDispatch();

  const { filter } = useFilterContext<BenefitFormTypes.Filter>();

  // Core orders the type column by enum number, which reads as random; the reader sees the translated name, so
  // that sort is done here over the whole list. A company's benefits are few, so one bounded fetch holds them all.
  const filterFunc = useCallback(
    async (baseFilter: WintUtilsWebApi.Filter) => {
      const sortsByType =
        baseFilter.orderByProperty === BenefitTypeProperty || baseFilter.thenByProperty === BenefitTypeProperty;
      if (!sortsByType) {
        return benefitService.reportFilter(baseFilter, filter ?? {});
      }

      const perPage = baseFilter.numPerPage ?? CommonConstants.DefaultTableRowCount;
      const page = baseFilter.page ?? 0;
      const response = await benefitService.reportFilter(
        { ...baseFilter, page: 0, numPerPage: TypeSortFetchCap },
        filter ?? {}
      );

      const typeIsPrimary = baseFilter.orderByProperty === BenefitTypeProperty;
      const typeDescending = typeIsPrimary ? baseFilter.orderByDescending : baseFilter.thenByDescending;
      const compareGroup = TableHelper.getComparator(
        baseFilter.orderByDescending ? "desc" : "asc",
        camelize(baseFilter.orderByProperty ?? "")
      );
      const locale = LocalizationHelperInstance.intl.locale;
      const compareType = (
        a: WintSuperkollAPIModelWintSalaryBenefits.BenefitListItem,
        b: WintSuperkollAPIModelWintSalaryBenefits.BenefitListItem
      ) => {
        const order = getBenefitTypeName(a.benefitType).localeCompare(getBenefitTypeName(b.benefitType), locale);
        return typeDescending ? -order : order;
      };

      const sorted = TableHelper.tableSortAsync(response.data.items ?? [], (a, b) =>
        typeIsPrimary ? compareType(a, b) : compareGroup(a, b) || compareType(a, b)
      );

      return {
        ...response,
        data: {
          ...response.data,
          items: sorted.slice(page * perPage, (page + 1) * perPage),
          page,
          numPerPage: perPage,
        },
      };
    },
    [filter]
  );

  const { getBenefitUpdateDialog } = useBenefitDialog();

  const onRowClick = (row: WintSuperkollAPIModelWintSalaryBenefits.BenefitListItem) => {
    if (row.benefitId) {
      dispatch(displayDialog(getBenefitUpdateDialog(row.benefitId, reloadList)));
    }
  };

  return (
    <TableAsync<WintSuperkollAPIModelWintSalaryBenefits.BenefitListItem>
      tableRef={ref}
      reloadKey={reloadKey}
      filter={filterFunc}
      maxRowCount={10}
      pagination="advanced"
      orderByProperty="name"
      groupByProperty="appliesToAllEmployees"
      groupByDescending
      rowClickHandler={onRowClick}
      properties={{
        group: {
          formatter: (benefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitListItem) =>
            benefit.appliesToAllEmployees
              ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Benefit.AllEmployeeGroupTitle)
              : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Benefit.SpecificEmployeeGroupTitle),
        },
        employees: {
          description: TranslationKeys.Common.Employee,
          formatter: (benefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitListItem) =>
            benefit.appliesToAllEmployees ? (
              LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Benefit.AllEmployeeItemLabel)
            ) : (
              <MultiPersonNames
                profiles={benefit.employees?.map((e) => ({ id: e.personId, name: e.name ?? "" })) ?? []}
                maxProfiles={2}
              />
            ),
          maxWidth: 200,
          disableSort: true,
        },
        name: {
          description: TranslationKeys.Benefit.NameColumn,
        },
        benefitType: {
          description: TranslationKeys.Benefit.BenefitTypeColumn,
          formatter: (benefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitListItem) =>
            getBenefitTypeName(benefit.benefitType),
        },
        start: {
          description: TranslationKeys.Benefit.PeriodColumn,
          formatter: (benefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitListItem) =>
            benefit.end
              ? new StyledDateRange(
                  DateHelper.getDateFromYearMonth(benefit.start),
                  DateHelper.getDateFromYearMonth(benefit.end)
                )
              : new YearMonth(benefit.start),
        },
        benefitAmount: {
          description: TranslationKeys.Benefit.BenefitValueColumn,
          alignment: "right",
          formatter: (benefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitListItem) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, benefit.benefitAmount),
          className: cssExports.textBlack,
        },
      }}
    />
  );
};

export default forwardRef(BenefitList);
